
import { useCallback, useEffect, useState } from 'react';
import { bannersApi, type Banner } from '@/shared/api';
import { acquireSocket, releaseSocket } from '@/shared/api';
import { useAuthStore } from '@/features/auth';
import { isDismissed, markDismissed, pruneDismissed } from './dismissed';

let cached: Banner[] = [];

export function useBanners(): {
  banner: Banner | null;
  next: Banner | null;
  dismiss: (banner: Banner) => void;
} {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [banners, setBannersState] = useState<Banner[]>(cached);
  const setBanners = useCallback((list: Banner[]) => {
    cached = list;
    setBannersState(list);
  }, []);
  const [hiddenNow, setHiddenNow] = useState<string[]>([]);

  const load = useCallback(async () => {
    try {
      const res = isAuthenticated ? await bannersApi.mine() : await bannersApi.public();
      const list = res.data ?? [];
      setBanners(list);
      pruneDismissed(list.map((b) => b.id));
    } catch {
      setBanners([]);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [load]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    let socket: Awaited<ReturnType<typeof acquireSocket>> = null;
    const onChanged = () => void load();
    void acquireSocket().then((s) => {
      if (cancelled || !s) return;
      socket = s;
      s.on('banners_changed', onChanged);
    });
    return () => {
      cancelled = true;
      socket?.off('banners_changed', onChanged);
      releaseSocket();
    };
  }, [isAuthenticated, load]);

  const dismiss = useCallback((banner: Banner) => {
    if (banner.dismissible) markDismissed(banner.id, banner.updatedAt);
    setHiddenNow((prev) => (prev.includes(banner.id) ? prev : [...prev, banner.id]));
  }, []);

  const visible = banners.filter(
    (b) => !hiddenNow.includes(b.id) && !isDismissed(b.id, b.updatedAt),
  );

  return { banner: visible[0] ?? null, next: visible[1] ?? null, dismiss };
}
